import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiJson } from '../lib/api'
import Field from '../components/forms/Field'

interface ProfileResponse {
  player: { id: string; slug: string; name: string }
  references: Array<{ coach_id: string | null }>
}

interface MeResponse {
  profile: { role: string | null }
}

interface CoachDashboardData {
  coach: { id: string; current_team: string | null } | null
}

interface FormState {
  star_rating: number
  quote: string
  team: string
  season: string
}

function defaultSeason(): string {
  const now = new Date()
  const year = now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1
  const next = (year + 1).toString().slice(-2)
  return `${year}-${next}`
}

export default function ReferenceNew() {
  const navigate = useNavigate()
  const { slug } = useParams()
  const [player, setPlayer] = useState<ProfileResponse['player'] | null>(null)
  const [form, setForm] = useState<FormState>({
    star_rating: 5,
    quote: '',
    team: '',
    season: defaultSeason(),
  })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      navigate('/', { replace: true })
      return
    }
    async function load() {
      try {
        const [me, profileData, dashboard] = await Promise.all([
          apiJson<MeResponse>('/api/me'),
          apiJson<ProfileResponse>(`/api/profile/${encodeURIComponent(slug!)}`),
          apiJson<CoachDashboardData>('/api/dashboard/coach'),
        ])
        if (cancelled) return
        if (me.profile.role !== 'coach') {
          navigate('/', { replace: true })
          return
        }
        if (!dashboard.coach) {
          navigate('/onboarding/coach', { replace: true })
          return
        }
        // Already wrote one?
        const alreadyWrote = profileData.references.some(
          (ref) => ref.coach_id === dashboard.coach!.id,
        )
        if (alreadyWrote) {
          navigate(`/players/${slug}`, { replace: true })
          return
        }
        setPlayer(profileData.player)
        setForm(f => ({ ...f, team: dashboard.coach!.current_team ?? '' }))
        setLoading(false)
      } catch {
        if (cancelled) return
        navigate('/', { replace: true })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug, navigate])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!player || submitting) return
    setSubmitting(true)
    setErr(null)
    if (!/^\d{4}-\d{2}$/.test(form.season.trim())) {
      setErr('Season must look like "2025-26".')
      setSubmitting(false)
      return
    }
    if (!form.team.trim()) {
      setErr('Team is required.')
      setSubmitting(false)
      return
    }
    if (!form.quote.trim()) {
      setErr('Quote is required.')
      setSubmitting(false)
      return
    }
    try {
      await apiJson('/api/references', {
        method: 'POST',
        body: JSON.stringify({
          player_id: player.id,
          star_rating: form.star_rating,
          quote: form.quote.trim(),
          team: form.team.trim(),
          season: form.season.trim(),
        }),
      })
      navigate(`/players/${player.slug}`, { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setSubmitting(false)
    }
  }

  if (loading || !player) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-hoop-text-3">Loading…</p>
      </main>
    )
  }

  return (
    <main className="max-w-xl mx-auto px-6 py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-hoop-text">
          Write a reference
        </h1>
        <p className="text-hoop-text-3 text-sm mt-1">
          For{' '}
          <span className="font-medium text-hoop-text-2">
            {player.name}
          </span>
          . References are visible to anyone viewing this player's profile. You can
          delete a reference later, but you can't edit it.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <Field label="Star rating" required>
          <div className="flex items-center gap-1 text-2xl">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => update('star_rating', n)}
                aria-label={`${n} of 5 stars`}
                className={
                  'leading-none transition ' +
                  (n <= form.star_rating
                    ? 'text-hoop-star'
                    : 'text-hoop-text-3 hover:text-hoop-star')
                }
              >
                ★
              </button>
            ))}
          </div>
        </Field>

        <Field label="Quote" required hint="What stood out about this player to you?">
          <textarea
            required
            rows={4}
            value={form.quote}
            onChange={e => update('quote', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Team" required hint="The team you coached when you saw this player.">
          <input
            type="text"
            required
            value={form.team}
            onChange={e => update('team', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Season" required hint="Format: 'YYYY-YY' (e.g. 2025-26).">
          <input
            type="text"
            required
            pattern="\d{4}-\d{2}"
            value={form.season}
            onChange={e => update('season', e.target.value)}
            className={inputClass}
          />
        </Field>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Submit reference'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/players/${player.slug}`)}
            className="text-hoop-text-2 hover:text-hoop-text text-sm"
          >
            Cancel
          </button>
          {err && (
            <p className="text-sm text-hoop-red">{err}</p>
          )}
        </div>
      </form>
    </main>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'
